import React from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, Link,
  Stack, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import { Code } from 'dashboard/components/common';
import {
  STATUS_COLORS, STATUS_LABEL,
  blockExplorerUrl, copyToClipboard, truncateAddr,
} from './utils';

/**
 * Single NFT collection row in the studio list.
 *
 * Shows name + symbol + status chip, deploy / retry buttons gated by
 * status, edit + delete, plus on-chain metadata (contract type, chain,
 * supply, royalty, address with block-explorer link).
 *
 * `deploying === collection._id` disables both Deploy and Retry to
 * prevent double-submits during the on-chain transaction.
 */
export default function CollectionCard({ collection: c, deploying, onDeploy, onEdit, onDelete }) {
  const explorer = blockExplorerUrl(c.chain_id, c.contract_address);

  return (
    <Box
      sx={{
        border: '1px solid', borderColor: 'divider',
        borderRadius: '12px', p: '14px', mb: '12px',
      }}
    >
      <Stack
        direction='row'
        justifyContent='space-between' flexWrap='wrap'
        gap='10px' alignItems='center'
        mb='8px'
      >
        <Stack direction='row' spacing='10px' alignItems='center' flexWrap='wrap'>
          <Typography fontWeight='700'>{c.name}</Typography>
          <Chip size='small' label={c.symbol} />
          <Chip
            size='small'
            color={STATUS_COLORS[c.status] || 'default'}
            icon={c.status === 'deploying' ? <CircularProgress size={12} /> : undefined}
            label={STATUS_LABEL[c.status] || c.status}
          />
        </Stack>
        <Stack direction='row' spacing='4px' alignItems='center' flexWrap='wrap'>
          {c.status === 'draft' && (
            <Button
              size='small' variant='contained'
              onClick={() => onDeploy(c)}
              disabled={deploying === c._id}
            >
              {deploying === c._id ? 'Deploying…' : 'Deploy'}
            </Button>
          )}
          {c.status === 'failed' && (
            <Tooltip title={c.last_error || 'Last deploy failed'}>
              <Button
                size='small' variant='contained' color='warning'
                startIcon={<ReplayIcon />}
                onClick={() => onDeploy(c)}
                disabled={deploying === c._id}
              >
                {deploying === c._id ? 'Retrying…' : 'Retry'}
              </Button>
            </Tooltip>
          )}
          <Tooltip title='Edit'>
            <IconButton size='small' onClick={() => onEdit(c)}>
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          {c.status !== 'deployed' && (
            <Tooltip title='Delete'>
              <IconButton size='small' color='error' onClick={() => onDelete(c)}>
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing='14px' flexWrap='wrap'>
        <Typography variant='body2' color='text.secondary'>
          Standard: <Code>{c.contract_type}</Code>
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          Chain: <Code>{c.chain_id}</Code>
        </Typography>
        {c.max_supply > 0 && (
          <Typography variant='body2' color='text.secondary'>
            Supply cap: <Code>{c.max_supply}</Code>
          </Typography>
        )}
        {c.royalty_basis_points > 0 && (
          <Typography variant='body2' color='text.secondary'>
            Royalty: <Code>{c.royalty_basis_points / 100}%</Code>
          </Typography>
        )}
        {c.drop_strategy === 'seadrop' && <Chip size='small' color='secondary' label='SeaDrop' />}
      </Stack>

      {c.contract_address && (
        <Stack direction='row' alignItems='center' spacing='8px' flexWrap='wrap' mt='10px'>
          <Code>{truncateAddr(c.contract_address)}</Code>
          <Tooltip title='Copy address'>
            <IconButton size='small' onClick={() => copyToClipboard(c.contract_address)}>
              <ContentCopyIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          {explorer && (
            <Link href={explorer} target='_blank' rel='noopener noreferrer' underline='hover'>
              <Stack direction='row' alignItems='center' spacing='4px'>
                <Typography variant='caption'>View on explorer</Typography>
                <OpenInNewIcon fontSize='inherit' />
              </Stack>
            </Link>
          )}
        </Stack>
      )}

      {c.status === 'failed' && c.last_error && (
        <Typography variant='caption' color='error.main' mt='8px' display='block'>
          {c.last_error}
        </Typography>
      )}
    </Box>
  );
}
